import type { MapBounds } from '../types';
import { geocellsForBounds, GEOCELL_FINE_SIZE } from '../utils/geocell';

/**
 * Danske parkeringsplasser fra GeoDanmark, servert som STATISKE geocellefiler.
 *
 * HVORFOR IKKE FIRESTORE. Målt på ekte data: en 50 km søkesirkel rundt
 * København treffer 11 572 parkeringsobjekter over 200 m². `AREA_SAFETY_CAP` er
 * 12 000, så parkeringen alene ville sprengt taket før én ekte plass var lest —
 * samme trunkering som tømte kartet i august. Og en 5 km sirkel rundt Odense
 * treffer 1 208, så zoom 14 ville gått fra 21 til ~1 200 lesninger.
 *
 * Filene ligger på SAMME 0,5°-rutenett som markørene (`utils/geocell.ts`), så
 * søkesirkelen begrenser hva vi henter akkurat som for plasser — men uten å
 * koste en eneste Firestore-lesning. Hele Danmark er 0,37 MB gzippet.
 *
 * Bygges av `scripts/build-parking-tiles-dk.mjs`.
 */

/** Én parkeringsplass: koordinat og areal i kvadratmeter. */
export interface DkParkingSpot {
  lat: number;
  lon: number;
  areaM2: number;
}

/** Rå radform i cellefilene. Kompakt med vilje — feltnavn ville tredoblet fila. */
type RawRow = [number, number, number];

export const PARKING_BASE = '/parking-dk';

/**
 * Tak på antall celler per henting. Én celle er ~0,5° (55 × 30 km), så 12 celler
 * dekker mer enn den største søkesirkelen på 50 km. Uten taket ville et
 * utzoomet Europa-utsnitt hentet alle 55 filene på én gang.
 */
export const MAX_CELLS_PER_FETCH = 12;

const cellCache = new Map<string, DkParkingSpot[]>();
let indexPromise: Promise<Set<string>> | null = null;

/** Nullstiller cachen. Bare for tester. */
export function __resetParkingCache(): void {
  cellCache.clear();
  indexPromise = null;
}

/**
 * Cellene som FINNES. Uten indeksen ville hver panorering over hav og Tyskland
 * gitt en 404 per celle, som både støyer i konsollen og koster en rundtur.
 */
export async function fetchParkingIndex(): Promise<Set<string>> {
  if (!indexPromise) {
    indexPromise = fetch(`${PARKING_BASE}/index.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: { celler?: string[] }) => new Set(j.celler || []))
      .catch(() => {
        // Nullstill, så neste forsøk prøver på nytt i stedet for å cache feilen.
        indexPromise = null;
        return new Set<string>();
      });
  }
  return indexPromise;
}

/** Én celle, hentet én gang. Filene endrer seg bare ved deploy. */
export async function fetchParkingCell(cellId: string): Promise<DkParkingSpot[]> {
  const cached = cellCache.get(cellId);
  if (cached) return cached;
  try {
    const res = await fetch(`${PARKING_BASE}/${cellId}.json`);
    if (!res.ok) { cellCache.set(cellId, []); return []; }
    const rader: RawRow[] = await res.json();
    const ut = (Array.isArray(rader) ? rader : [])
      .filter((r) => Array.isArray(r) && Number.isFinite(r[0]) && Number.isFinite(r[1]))
      .map((r) => ({ lat: r[0], lon: r[1], areaM2: Number(r[2]) || 0 }));
    cellCache.set(cellId, ut);
    return ut;
  } catch {
    // IKKE cache en nettverksfeil — da ville cellen vært tom for alltid.
    return [];
  }
}

/** Cellene et utsnitt berører, som faktisk finnes, opp til taket. */
export async function cellsForBounds(bounds: MapBounds): Promise<string[]> {
  const finnes = await fetchParkingIndex();
  if (!finnes.size) return [];
  return geocellsForBounds(bounds, GEOCELL_FINE_SIZE)
    .filter((c) => finnes.has(c))
    .slice(0, MAX_CELLS_PER_FETCH);
}

/** Alt innenfor utsnittet. Cellene er større enn utsnittet, så vi klipper. */
export async function fetchParkingForBounds(bounds: MapBounds): Promise<DkParkingSpot[]> {
  const celler = await cellsForBounds(bounds);
  if (!celler.length) return [];
  const deler = await Promise.all(celler.map(fetchParkingCell));
  return deler.flat().filter((p) =>
    p.lat >= bounds.latMin && p.lat <= bounds.latMax
    && p.lon >= bounds.lonMin && p.lon <= bounds.lonMax);
}
